import { useEffect, useMemo, useState } from "react";
import { SITES, siteCrest, subscribeSiteCrests, siteAccentHex, deptAccentHex, subscribeSiteTags, subscribeDeptTags } from "@/lib/sites";
import { BarChart3, Layers, CheckCircle2 } from "lucide-react";

const DEPTS = ["IT", "Maintenance", "Custodial", "Mechanic"];

export default function SiteHotspotChart({ openBySite = {}, openBySiteDept = {}, closedBySite = {}, closedBySiteDept = {} }) {
  // 'total' = open aggregate · 'split' = open split by dept · 'closed' = Resolved+Closed aggregate
  const [mode, setMode] = useState("total");
  const [, setTick] = useState(0);
  useEffect(() => subscribeSiteCrests(() => setTick((n) => n + 1)), []);
  useEffect(() => subscribeSiteTags(() => setTick((n) => n + 1)), []);
  useEffect(() => subscribeDeptTags(() => setTick((n) => n + 1)), []);

  const isClosed = mode === "closed";
  const split = mode === "split";
  const bySiteSrc = isClosed ? closedBySite : openBySite;
  const bySiteDeptSrc = isClosed ? closedBySiteDept : openBySiteDept;

  const rows = useMemo(() => {
    const data = SITES.map((s) => {
      const bySlice = bySiteDeptSrc[s.code] || {};
      const IT = bySlice.IT || 0;
      const Maintenance = bySlice.Maintenance || 0;
      const Custodial = bySlice.Custodial || 0;
      const Mechanic = bySlice.Mechanic || 0;
      const count = bySiteSrc[s.code] ?? (IT + Maintenance + Custodial + Mechanic);
      return { code: s.code, label: s.label, short: s.short, count, slices: { IT, Maintenance, Custodial, Mechanic } };
    });
    return { all: data, max: Math.max(1, ...data.map((d) => d.count)) };
  }, [bySiteSrc, bySiteDeptSrc]);

  const totalCount = rows.all.reduce((a, r) => a + r.count, 0);
  const headerTitle = isClosed ? "Closed & Resolved tickets by site" : "Open tickets by site";
  const headerSub = isClosed
    ? `${totalCount} closed or resolved across the district · sorted by school`
    : `${totalCount} open across the district · ${split ? "split by department" : "sorted by school"}`;
  const bigLabel = isClosed ? "total closed / resolved" : "total open";

  return (
    <div className="bg-white border border-zinc-200 rounded-md p-6" data-testid="site-hotspot-chart">
      {/* Header: stacks on mobile (full-width title, then count+label inline below);
          on ≥sm the count sits top-right in its own column as before. */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0 sm:flex-1">
          <div className="text-xs uppercase tracking-[0.1em] text-zinc-500 flex items-center gap-2">
            <BarChart3 className="h-3.5 w-3.5" /> Hotspots
          </div>
          <h3 className="font-heading text-lg font-semibold text-zinc-950 mt-1">{headerTitle}</h3>
          <p className="text-xs text-zinc-500 mt-0.5">{headerSub}</p>
        </div>
        <div className="flex items-baseline gap-2 sm:flex-col sm:items-end sm:gap-0 shrink-0 sm:text-right">
          <div className="font-heading text-3xl sm:text-3xl font-light tracking-tighter text-zinc-950 leading-none" data-testid="hotspot-total-count">{totalCount}</div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 sm:mt-0.5 whitespace-nowrap" data-testid="hotspot-total-label">{bigLabel}</div>
        </div>
      </div>

      {/* Segmented control — its own row so it can't compress the header. Full width on mobile, aligned right on desktop. */}
      <div className="mt-4 flex sm:justify-end">
        <div className="inline-flex items-center border border-zinc-200 rounded-md overflow-hidden text-xs w-full sm:w-auto" data-testid="hotspot-mode-toggle">
          <button
            type="button"
            onClick={() => setMode("total")}
            data-testid="hotspot-mode-total"
            className={`flex-1 sm:flex-none px-3 py-1.5 inline-flex items-center justify-center transition-colors duration-200 ${mode === "total" ? "bg-zinc-950 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
          >
            Total
          </button>
          <button
            type="button"
            onClick={() => setMode("split")}
            data-testid="hotspot-mode-split"
            className={`flex-1 sm:flex-none px-3 py-1.5 inline-flex items-center justify-center gap-1 transition-colors duration-200 border-l border-zinc-200 ${mode === "split" ? "bg-zinc-950 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
          >
            <Layers className="h-3 w-3" /> Split
          </button>
          <button
            type="button"
            onClick={() => setMode("closed")}
            data-testid="hotspot-mode-closed"
            className={`flex-1 sm:flex-none px-3 py-1.5 inline-flex items-center justify-center gap-1 transition-colors duration-200 border-l border-zinc-200 ${mode === "closed" ? "bg-emerald-600 text-white" : "text-zinc-600 hover:bg-zinc-50"}`}
          >
            <CheckCircle2 className="h-3 w-3" /> Closed
          </button>
        </div>
      </div>

      {/* Legend (only in split mode) */}
      {split && (
        <div className="mt-4 flex items-center gap-4 flex-wrap text-xs">
          {DEPTS.map((d) => (
            <div key={d} className="flex items-center gap-1.5" data-testid={`hotspot-legend-${d.toLowerCase()}`}>
              <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: deptAccentHex(d) }} />
              <span className="text-zinc-700">{d}</span>
            </div>
          ))}
        </div>
      )}

      <ul className="mt-6 space-y-3">
        {rows.all.map((r) => {
          const pct = Math.round((r.count / rows.max) * 100);
          const color = siteAccentHex(r.code);
          const isHotspot = r.count > 0 && r.count >= Math.max(3, rows.max * 0.7);

          const denom = Math.max(1, r.slices.IT + r.slices.Maintenance + r.slices.Custodial + r.slices.Mechanic);
          const w = (n) => (r.count === 0 ? 0 : (n / denom) * Math.max(pct, 3));

          return (
            <li key={r.code} className="group" data-testid={`hotspot-row-${r.code}`}>
              <div className="flex items-center justify-between text-xs mb-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="inline-block h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  {siteCrest(r.code) && (
                    <img
                      src={siteCrest(r.code)}
                      alt=""
                      className="h-4 w-4 rounded-sm object-contain shrink-0 border border-zinc-200 bg-white"
                      data-testid={`hotspot-crest-${r.code}`}
                    />
                  )}
                  <span className="text-zinc-800 font-medium truncate">{r.label}</span>
                  {isHotspot && (
                    <span className="text-[9px] uppercase tracking-[0.15em] bg-red-100 text-red-700 border border-red-200 px-1.5 py-0.5 rounded-full">
                      Hotspot
                    </span>
                  )}
                </div>
                <span className="font-mono text-xs text-zinc-700 shrink-0 tabular-nums">
                  {split && r.count > 0
                    ? `${r.slices.IT} · ${r.slices.Maintenance} · ${r.slices.Custodial} · ${r.slices.Mechanic}  (${r.count})`
                    : r.count}
                </span>
              </div>
              <div className="h-2 w-full bg-zinc-100 rounded-full overflow-hidden flex">
                {split ? (
                  <>
                    <div
                      className="h-full transition-[width] duration-500 ease-out"
                      style={{ width: `${w(r.slices.IT)}%`, backgroundColor: deptAccentHex("IT") }}
                      title={`IT: ${r.slices.IT}`}
                    />
                    <div
                      className="h-full transition-[width] duration-500 ease-out"
                      style={{ width: `${w(r.slices.Maintenance)}%`, backgroundColor: deptAccentHex("Maintenance") }}
                      title={`Maintenance: ${r.slices.Maintenance}`}
                    />
                    <div
                      className="h-full transition-[width] duration-500 ease-out"
                      style={{ width: `${w(r.slices.Custodial)}%`, backgroundColor: deptAccentHex("Custodial") }}
                      title={`Custodial: ${r.slices.Custodial}`}
                    />
                    <div
                      className="h-full transition-[width] duration-500 ease-out"
                      style={{ width: `${w(r.slices.Mechanic)}%`, backgroundColor: deptAccentHex("Mechanic") }}
                      title={`Mechanic: ${r.slices.Mechanic}`}
                    />
                  </>
                ) : (
                  <div
                    className="h-full rounded-full transition-[width] duration-500 ease-out"
                    style={{ width: `${r.count === 0 ? 0 : Math.max(pct, 3)}%`, backgroundColor: color }}
                  />
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
